"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect } from "react";
import { useClearRefinements, useCurrentRefinements } from "react-instantsearch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import type { PlpFilterGroupConfig } from "@/lib/contentful/filterGroups";
import { useConditionalGroupVisible } from "./PlpConditionalVisibility";
import { useFilterGroups } from "./PlpFilterGroupsContext";
import { PlpProductTypeFilter } from "./PlpProductTypeFilter";
import { PlpRefinementGroup } from "./PlpRefinementGroup";

// Shared shape forwarded down the sidebar / drawer / results tree. Keeps the
// excludeAttributes + bakedAttributes pair from drifting between consumers.
export interface PlpFilterScope {
  excludeAttributes?: readonly string[];
  bakedAttributes?: Readonly<Record<string, string>>;
}
type Props = PlpFilterScope;
interface GroupSlotProps {
  group: PlpFilterGroupConfig;
  isLast: boolean;
  bakedAttributes?: Readonly<Record<string, string>>;
}
function PlpProductTypeSlot(t0) {
  const $ = _c(10);
  const {
    group,
    isLast
  } = t0;
  const t1 = isLast ? "border-b-0" : "border-border";
  const t2 = `plp-filter-group-${group.slug}`;
  let t3;
  if ($[0] !== group.label) {
    t3 = <AccordionTrigger className="text-2xl font-semibold hover:no-underline">{group.label}</AccordionTrigger>;
    $[0] = group.label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== group.values) {
    t4 = <AccordionContent><PlpProductTypeFilter values={group.values} /></AccordionContent>;
    $[2] = group.values;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== group.slug || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <AccordionItem value={group.slug} className={t1} data-testid={t2}>{t3}{t4}</AccordionItem>;
    $[4] = group.slug;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function ConditionalGroupSlot(t0) {
  const $ = _c(17);
  const {
    group,
    isLast,
    bakedAttributes
  } = t0;
  const visible = useConditionalGroupVisible(group, bakedAttributes);
  let t1;
  if ($[0] !== group.slug) {
    t1 = {
      includedAttributes: [group.slug]
    };
    $[0] = group.slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    items: ownRefinements
  } = useCurrentRefinements(t1);
  let t2;
  if ($[2] !== group.slug) {
    t2 = {
      includedAttributes: [group.slug]
    };
    $[2] = group.slug;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    refine: clearOwn
  } = useClearRefinements(t2);
  let t3;
  if ($[4] !== ownRefinements) {
    t3 = ownRefinements.some(_temp);
    $[4] = ownRefinements;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasActiveOwnRefinements = t3;
  let t4;
  let t5;
  if ($[6] !== clearOwn || $[7] !== hasActiveOwnRefinements || $[8] !== visible) {
    t4 = () => {
      if (visible) {
        return;
      }
      if (!hasActiveOwnRefinements) {
        return;
      }
      clearOwn();
    };
    t5 = [visible, hasActiveOwnRefinements, clearOwn];
    $[6] = clearOwn;
    $[7] = hasActiveOwnRefinements;
    $[8] = visible;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useLayoutEffect(t4, t5);
  if (!visible) {
    return null;
  }
  if (group.slug === "productType") {
    let t6;
    if ($[11] !== group || $[12] !== isLast) {
      t6 = <PlpProductTypeSlot group={group} isLast={isLast} />;
      $[11] = group;
      $[12] = isLast;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t6;
  if ($[14] !== group || $[15] !== isLast) {
    t6 = <PlpRefinementGroup group={group} isLast={isLast} />;
    $[14] = group;
    $[15] = isLast;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp(item) {
  return item.refinements.length > 0;
}
export function PlpFilterSidebar(t0) {
  const $ = _c(14);
  const {
    excludeAttributes,
    bakedAttributes
  } = t0;
  const groups = useFilterGroups();
  let t1;
  if ($[0] !== excludeAttributes || $[1] !== groups) {
    t1 = excludeAttributes ? groups.filter(group => !excludeAttributes.includes(group.slug)) : groups;
    $[0] = excludeAttributes;
    $[1] = groups;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleGroups = t1;
  let t2;
  if ($[3] !== visibleGroups) {
    t2 = visibleGroups.map(_temp2);
    $[3] = visibleGroups;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const defaultOpen = t2;
  let t3;
  if ($[5] !== bakedAttributes || $[6] !== visibleGroups) {
    let t4;
    if ($[8] !== bakedAttributes || $[9] !== visibleGroups.length) {
      t4 = (group_1, index) => <ConditionalGroupSlot key={group_1.slug} group={group_1} isLast={index === visibleGroups.length - 1} bakedAttributes={bakedAttributes} />;
      $[8] = bakedAttributes;
      $[9] = visibleGroups.length;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = visibleGroups.map(t4);
    $[5] = bakedAttributes;
    $[6] = visibleGroups;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[11] !== defaultOpen || $[12] !== t3) {
    t4 = <div data-testid="plp-filter-sidebar"><Card className="w-full rounded-md gap-0 py-0"><CardContent className="p-6"><Accordion type="multiple" defaultValue={defaultOpen}>{t3}</Accordion></CardContent></Card></div>;
    $[11] = defaultOpen;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp2(group_0) {
  return group_0.slug;
}